import { gsap } from 'gsap';
import * as THREE from 'three';

export class CameraController {
    constructor(app) {
        this.app = app;
        this.camera = app.camera;
        this.isAnimating = false;
        
        this.defaultPosition = { x: -1, y: 1, z: 3 };
        this.defaultTarget = { x: -4, y: 0, z: -3 };
        
        this.setupControls();
    }

    setupControls() {
        this.mouse = new THREE.Vector2();
        this.targetMouse = new THREE.Vector2();

        // Only enable mouse parallax on desktop devices
        window.addEventListener('mousemove', (event) => {
            // Skip mouse tracking on mobile to save performance
            if (this.app.deviceDetector.isMobile || this.app.deviceDetector.isMobileSize()) {
                return;
            }

            this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
            this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
        });
    }

    update() {
        // Skip camera control if gallery is controlling the camera or if we're animating
        if (!this.isAnimating && !this.isGalleryControllingCamera()) {
            // Disable parallax effect on mobile devices for better performance
            const isMobile = this.app.deviceDetector.isMobile || this.app.deviceDetector.isMobileSize();

            if (!isMobile) {
                // Apply parallax effect on desktop
                this.targetMouse.lerp(this.mouse, 0.05);

                const parallaxStrength = 0.1;
                const basePosition = { x: this.defaultPosition.x, y: this.defaultPosition.y };
                this.camera.position.x += (basePosition.x + this.targetMouse.x * parallaxStrength - this.camera.position.x) * 0.05;
                this.camera.position.y += (basePosition.y + this.targetMouse.y * parallaxStrength - this.camera.position.y) * 0.05;
            } else {
                // On mobile, keep camera at default position (no parallax)
                this.camera.position.x += (this.defaultPosition.x - this.camera.position.x) * 0.05;
                this.camera.position.y += (this.defaultPosition.y - this.camera.position.y) * 0.05;
            }

            this.camera.lookAt(this.defaultTarget.x, this.defaultTarget.y, this.defaultTarget.z);
        }
    }

    isGalleryControllingCamera() {
        // Check if gallery scene is zoomed in or animating
        return this.app.galleryScene && 
               (this.app.galleryScene.isZoomedIn || this.app.galleryScene.isZoomAnimating);
    }

    moveTo(position, duration = 1, target = null, onComplete = null) {
        if (this.isAnimating) return;
        
        this.isAnimating = true;
        
        const targetPos = target || this.defaultTarget;
        
        gsap.to(this.camera.position, {
            x: position.x,
            y: position.y,
            z: position.z,
            duration: duration,
            ease: "power2.inOut",
            onUpdate: () => {
                this.camera.lookAt(targetPos.x, targetPos.y, targetPos.z);
            },
            onComplete: () => {
                this.isAnimating = false;
                if (onComplete) onComplete();
            }
        });
    }

    journeyToGallery(onComplete) {
        if (this.isAnimating) {
            console.log('Camera already animating, forcing completion');
            this.isAnimating = false;
            if (onComplete) onComplete();
            return;
        }
        
        console.log('Starting simplified camera journey to gallery');
        this.isAnimating = true;
        
        // Much simpler animation - just move to gallery position
        gsap.to(this.camera.position, {
            x: 0,
            y: 2,
            z: 5,
            duration: 2,
            ease: "power2.inOut",
            onUpdate: () => {
                this.camera.lookAt(0, 0, 0);
            },
            onComplete: () => {
                console.log('Simplified camera journey completed');
                this.isAnimating = false;
                if (onComplete) onComplete();
            }
        });
    }

    lookAt(target, duration = 1, onComplete = null) {
        if (this.isAnimating) return;
        
        this.isAnimating = true;
        
        const currentTarget = new THREE.Vector3();
        this.camera.getWorldDirection(currentTarget);
        currentTarget.multiplyScalar(-1);
        currentTarget.add(this.camera.position);
        
        const targetVector = new THREE.Vector3(target.x, target.y, target.z);
        
        gsap.to(currentTarget, {
            x: targetVector.x,
            y: targetVector.y,
            z: targetVector.z,
            duration: duration,
            ease: "power2.inOut",
            onUpdate: () => {
                this.camera.lookAt(currentTarget);
            },
            onComplete: () => {
                this.isAnimating = false;
                if (onComplete) onComplete();
            }
        });
    }

    shake(intensity = 0.1, duration = 0.5) {
        if (this.isAnimating) return;
        
        const originalPosition = {
            x: this.camera.position.x,
            y: this.camera.position.y,
            z: this.camera.position.z
        };
        
        this.isAnimating = true;
        
        gsap.to(this.camera.position, {
            x: `+=${Math.random() * intensity - intensity/2}`,
            y: `+=${Math.random() * intensity - intensity/2}`,
            duration: 0.1,
            repeat: Math.floor(duration / 0.1),
            yoyo: true,
            ease: "power2.inOut",
            onComplete: () => {
                gsap.to(this.camera.position, {
                    x: originalPosition.x,
                    y: originalPosition.y,
                    z: originalPosition.z,
                    duration: 0.2,
                    ease: "power2.out",
                    onComplete: () => {
                        this.isAnimating = false;
                    }
                });
            }
        });
    }

    reset() {
        this.moveTo(this.defaultPosition, 1.5, this.defaultTarget);
    }

    setDefaultPosition(position, target = null) {
        this.defaultPosition = { ...position };
        if (target) {
            this.defaultTarget = { ...target };
        }
    }

    getScreenPosition(worldPosition) {
        const vector = worldPosition.clone();
        vector.project(this.camera);
        
        return {
            x: (vector.x * 0.5 + 0.5) * window.innerWidth,
            y: (vector.y * -0.5 + 0.5) * window.innerHeight
        };
    }
}